import React, { useState } from 'react'
import { nanoid } from 'nanoid'
import { observer } from 'mobx-react-lite'
import { Button, IconButton, Typography } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { uiStore } from 'shared/store/uiStore'
import { PhoneApi } from 'entities/Phone'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { NumbersPath } from 'pages/settings/pages/numbers/route/numbersPath'
import { useNumbersLayerContext } from 'pages/settings/pages/numbers/context/numbersLayerContext'

type IImportTwilioNumbersActionProps = {
  variant?: 'button'
  manageNumberWrapper: (fn: () => void, options?: { skipCanceledCheck?: boolean }) => void
}

export const ImportTwilioNumbersAction = observer(
  ({ variant, manageNumberWrapper }: IImportTwilioNumbersActionProps) => {
    const [loading, setLoading] = useState(false)
    const numbersLayerStore = useNumbersLayerContext()
    const entities = useEntitiesContext()
    const { organizationStore } = entities
    const { isNumbersGracePeriod, isTwilioNumberVendor } = organizationStore

    const onRefresh = async () => {
      try {
        setLoading(true)
        const { data } = await PhoneApi.getIncomingTwilioNumbers()

        if (data.data.length) {
          numbersLayerStore.initImportTwilioNumbersStore(data.data)
        } else if (isTwilioNumberVendor) {
          modalStore.addModal({
            id: nanoid(),
            type: ModalTypeList.INFO,
            title: 'No new numbers available to import.',
            desc: 'All your Twilio numbers are already in Salesmsg',
            closeActionButtonProps: {
              text: 'Done',
            },
          })
        } else {
          uiStore.changeRoute({ path: `/${NumbersPath.importTwilioNumbers.empty}` })
        }
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }

    const handleRefresh = () => {
      manageNumberWrapper(onRefresh, { skipCanceledCheck: true })
    }

    if (variant === 'button') {
      return (
        <Button
          contained={'secondary'}
          size={'medium'}
          onClick={handleRefresh}
          text={'Import numbers'}
          loading={loading}
          disabled={isNumbersGracePeriod}
          tooltipProps={
            isNumbersGracePeriod
              ? {
                  label: (
                    <Typography
                      variant={'body-md-regular'}
                      ellipsis
                      color={'var(--content-primary-primary-inverted)'}
                      ariaLabel={'Grace Period'}
                    >
                      Take care of unpaid licenses
                      <br /> before adding new numbers
                    </Typography>
                  ),
                  width: 233,
                  placement: 'top',
                }
              : undefined
          }
        />
      )
    }

    if (isNumbersGracePeriod) return null

    return (
      <IconButton
        variant={'icon'}
        color={'tertiary'}
        size={'small'}
        ariaLabel='Refresh_numbers'
        onClick={handleRefresh}
        icon={'refresh'}
        loading={loading}
        tooltipProps={{
          label: 'Refresh',
          placement: 'top',
        }}
      />
    )
  }
)
